// src/store/modules/portfolio.js
import axios from "@/api/client";

const state = {
  // letztes geladenes Ergebnis
  portfolioAtDate: null,

  // Map: "YYYY-MM-DD" -> Ergebnis vom Backend
  portfoliosByDate: {},

  // optional: Liste in Reihenfolge (für Tabelle/Chart)
  portfolioTimeline: [],

  loading: false,
  error: null,

  tradingDiscipline: null,
  tradingDisciplineLoading: false,
  tradingDisciplineError: null,

  // ========= DAILY TRADES (NEU) =========
  dailyTrades: null,
  dailyTradesLoading: false,
  dailyTradesError: null,

  // ========= DAILY TRADES ALL (NEU) =========
  dailyTradesAll: null,
  dailyTradesAllLoading: false,
  dailyTradesAllError: null,
};

const getters = {
  statePortfolioAtDate: (state) => state.portfolioAtDate,
  statePortfoliosByDate: (state) => state.portfoliosByDate,
  statePortfolioTimeline: (state) => state.portfolioTimeline,
  statePortfolioLoading: (state) => state.loading,
  statePortfolioError: (state) => state.error,

  stateTradingDiscipline: (state) => state.tradingDiscipline,
  stateTradingDisciplineLoading: (state) => state.tradingDisciplineLoading,
  stateTradingDisciplineError: (state) => state.tradingDisciplineError,

  // ========= DAILY TRADES (NEU) =========
  stateDailyTrades: (state) => state.dailyTrades,
  stateDailyTradesLoading: (state) => state.dailyTradesLoading,
  stateDailyTradesError: (state) => state.dailyTradesError,

  // ========= DAILY TRADES ALL (NEU) =========
  stateDailyTradesAll: (state) => state.dailyTradesAll,
  stateDailyTradesAllLoading: (state) => state.dailyTradesAllLoading,
  stateDailyTradesAllError: (state) => state.dailyTradesAllError,
};

function toYMD(dateLike) {
  // akzeptiert Date | string ("2026-01-10" oder ISO)
  if (!dateLike) return null;

  if (typeof dateLike === "string") {
    // Wenn ISO "2026-01-10T00:00:00" -> nur Datum nehmen
    return dateLike.slice(0, 10);
  }

  const d = dateLike;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function yearEndDates(yearsBack, includeToday = true) {
  const now = new Date();
  const y = now.getFullYear();

  const dates = [];
  for (let i = 0; i < yearsBack; i++) {
    const year = y - i;
    // Jahresende
    dates.push(`${year}-12-31`);
  }

  if (includeToday) {
    dates.unshift(toYMD(now));
  }

  // unique
  return [...new Set(dates)];
}

function buildSnapshotScoreUrl(path, payload = {}) {
  const params = new URLSearchParams();
  const from = toYMD(payload.from);
  const to = toYMD(payload.to);

  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (payload.period) params.set("period", payload.period);
  if (payload.basis) params.set("basis", payload.basis);
  if (payload.limit !== undefined && payload.limit !== null) {
    params.set("limit", String(payload.limit));
  }

  if (Array.isArray(payload.user_ids) && payload.user_ids.length) {
    payload.user_ids.forEach((id) => {
      params.append("user_ids", String(id));
    });
  }

  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

const actions = {
  async getTradingDiscipline({ commit }, payload = {}) {
    const from = toYMD(payload.from);
    const to = toYMD(payload.to);
    const basis = payload.basis || "count";
    const quick_exit_days = Number(payload.quick_exit_days ?? 7);
    const reentry_days = Number(payload.reentry_days ?? 10);

    if (!from || !to) return;

    commit("setTradingDisciplineLoading", true);
    commit("setTradingDisciplineError", null);

    try {
      const { data } = await axios.get(
        `analytics/trading-discipline?from=${encodeURIComponent(from)}&to=${encodeURIComponent(
          to
        )}&basis=${encodeURIComponent(basis)}&quick_exit_days=${encodeURIComponent(
          quick_exit_days
        )}&reentry_days=${encodeURIComponent(reentry_days)}`
      );

      commit("setTradingDiscipline", data);
      return data;
    } catch (e) {
      commit("setTradingDisciplineError", e);
      throw e;
    } finally {
      commit("setTradingDisciplineLoading", false);
    }
  },
  async getPortfolioSnapshotScores(_, payload = {}) {
    const endpoint = payload.kind === "loss" ? "worst-losses" : "best-gains";
    const { data } = await axios.get(
      buildSnapshotScoreUrl(`portfolio/snapshots/${endpoint}`, payload)
    );
    return data;
  },
    async getAvailablePortfolioUsersAtDate(_, stichtag) {
      const ymd = toYMD(stichtag);
      if (!ymd) return;

      const { data } = await axios.get(
        `admin/portfolios/available-users?stichtag=${encodeURIComponent(ymd)}`
      );
      return data;
    },


async getPortfolioAtDate({ commit }, payload) {
      // payload kann string sein oder { stichtag, user_ids }
      const stichtag = typeof payload === "string" ? payload : payload?.stichtag;
      const user_ids = typeof payload === "string" ? null : payload?.user_ids;
      const noCache = typeof payload === "string" ? false : payload?.noCache === true;
      const silent = typeof payload === "string" ? false : payload?.silent === true;

      const ymd = toYMD(stichtag);
      if (!ymd) return;

    if (!silent) {
      commit("setPortfolioLoading", true);
      commit("setPortfolioError", null);
    }

    try {
      // FastAPI nimmt datetime – wir geben YYYY-MM-DD rein,
      // Backend parsed das i.d.R. als 00:00:00
      //const { data } = await axios.get(`portfolio/at-date?stichtag=${encodeURIComponent(ymd)}`);
      let url = `portfolio/at-date?stichtag=${encodeURIComponent(ymd)}`;

        if (Array.isArray(user_ids) && user_ids.length) {
          // FastAPI List[int] Query: user_ids=1&user_ids=2...
          const q = user_ids.map((id) => `user_ids=${encodeURIComponent(id)}`).join("&");
          url += `&${q}`;
        }

        const { data } = await axios.get(url);


      if (!noCache) {
        commit("setPortfolioAtDate", data);
        commit("setPortfolioByDate", { dateKey: ymd, payload: data });
      }

      return data;
    } catch (e) {
      if (!silent) commit("setPortfolioError", e);
      throw e;
    } finally {
      if (!silent) commit("setPortfolioLoading", false);
    }
  },

  /**
   * Lädt mehrere Stichtage (z.B. Jahresenden) und baut eine Timeline für Tabellen/Charts.
   * payload: { yearsBack: 6, includeToday: true }
   */
  async getPortfolioYearEnds({ commit, dispatch }, payload = {}) {
    const yearsBack = Number(payload.yearsBack ?? 6);
    const includeToday = payload.includeToday !== false;

    const dates = yearEndDates(yearsBack, includeToday);

    commit("setPortfolioLoading", true);
    commit("setPortfolioError", null);
    commit("clearPortfolioTimeline");

    try {
      // parallel laden
      const results = await Promise.all(
        dates.map(async (d) => {
          const data = await dispatch("getPortfolioAtDate", d);
          return { date: d, data };
        })
      );

      // sortiert nach Datum absteigend (neu -> alt)
      results.sort((a, b) => (a.date < b.date ? 1 : -1));

      // Timeline = flache Liste: { stichtag, wert_gesamt, positionen_count }
      const timeline = results.map((r) => ({
        stichtag: r.date,
        wert_gesamt: r.data?.wert_gesamt ?? 0,
        positionen_count: Array.isArray(r.data?.positionen) ? r.data.positionen.length : 0,
        raw: r.data, // falls du pro Zeile auf Positionen zugreifen willst
      }));

      commit("setPortfolioTimeline", timeline);
      return timeline;
    } catch (e) {
      commit("setPortfolioError", e);
      throw e;
    } finally {
      commit("setPortfolioLoading", false);
    }
  },

  // ========= DAILY TRADES (NEU) =========
  async getDailyTrades({ commit }) {
    commit("setDailyTradesLoading", true);
    commit("setDailyTradesError", null);

    try {
      const { data } = await axios.get(`portfolio/daily-trades`);
      commit("setDailyTrades", data);
      return data;
    } catch (e) {
      commit("setDailyTradesError", e);
      throw e;
    } finally {
      commit("setDailyTradesLoading", false);
    }
  },

  // ========= DAILY TRADES ALL (NEU) =========
  async getDailyTradesAll({ commit }) {
    commit("setDailyTradesAllLoading", true);
    commit("setDailyTradesAllError", null);

    try {
      const { data } = await axios.get(`portfolio/daily-trades/all`);
      commit("setDailyTradesAll", data);
      return data;
    } catch (e) {
      commit("setDailyTradesAllError", e);
      throw e;
    } finally {
      commit("setDailyTradesAllLoading", false);
    }
  },
};

const mutations = {
  setPortfolioAtDate(state, payload) {
    state.portfolioAtDate = payload;
  },
  setPortfolioByDate(state, { dateKey, payload }) {
    state.portfoliosByDate = {
      ...state.portfoliosByDate,
      [dateKey]: payload,
    };
  },
  clearPortfolioTimeline(state) {
    state.portfolioTimeline = [];
  },
  setPortfolioTimeline(state, payload) {
    state.portfolioTimeline = payload;
  },
  setPortfolioLoading(state, value) {
    state.loading = !!value;
  },
  setPortfolioError(state, err) {
    state.error = err || null;
  },

  setTradingDiscipline(state, payload) {
    state.tradingDiscipline = payload;
  },
  setTradingDisciplineLoading(state, value) {
    state.tradingDisciplineLoading = !!value;
  },
  setTradingDisciplineError(state, err) {
    state.tradingDisciplineError = err || null;
  },

  // ========= DAILY TRADES (NEU) =========
  setDailyTrades(state, payload) {
    state.dailyTrades = payload;
  },
  setDailyTradesLoading(state, value) {
    state.dailyTradesLoading = !!value;
  },
  setDailyTradesError(state, err) {
    state.dailyTradesError = err || null;
  },

  // ========= DAILY TRADES ALL (NEU) =========
  setDailyTradesAll(state, payload) {
    state.dailyTradesAll = payload;
  },
  setDailyTradesAllLoading(state, value) {
    state.dailyTradesAllLoading = !!value;
  },
  setDailyTradesAllError(state, err) {
    state.dailyTradesAllError = err || null;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
